import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  Car,
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
  ListChecks,
  Lock,
  LogOut,
  Menu,
  ReceiptText,
  ShoppingBag,
  Sparkles,
  User,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { getPosClient, setApiLocked } from "../lib/api";
import { clearSession } from "../lib/auth";
import { FEATURE_INVENTORY, FEATURE_REPORTING, isFeatureEnabled } from "../lib/features";
import {
  POS_AI_CHAT,
  POS_AI_RECOMMENDATIONS_VIEW,
  POS_METRICS_VIEW_LOCATION,
  POS_METRICS_VIEW_ORG,
  POS_METRICS_VIEW_PARTNER,
  POS_ORDER_CREATE,
  POS_PAYMENT_INITIATE,
  POS_TRANSACTION_VIEW,
  POS_TRANSACTION_VIEW_OWN,
} from "../lib/permissions";
import { useFocusTrap } from "../lib/use-focus-trap";
import { useSidebarCollapse } from "../lib/use-sidebar-collapse";
import { useInactivityLock } from "../lib/use-inactivity-lock";
import { cn } from "../lib/utils";
import { BrandFavicon } from "./BrandFavicon";
import { BrandStyles } from "./BrandStyles";
import { Footer } from "./Footer";
import { LockScreen } from "./LockScreen";
import { ChatDrawer } from "./ai-chat/ChatDrawer";
import { useChatStream } from "../lib/use-chat-stream";

const CHAT_URL = import.meta.env.VITE_AI_CHAT_URL ?? "/pos/v1/ai/chat";
import { LanguagePicker } from "./LanguagePicker";
import { RecentTransactionsPanel } from "./RecentTransactionsPanel";
import { ScopeSwitcher } from "./ScopeSwitcher";
import { ThemePicker } from "./ThemePicker";
import { Alert } from "./ui/Alert";
import { MfaSetupGate } from "./profile/MfaSetupGate";
import { PinSetupGate } from "./profile/PinSetupGate";
import { PageSpinner } from "./ui/Spinner";

/**
 * App shell - full-width topbar (brand + utility controls) above a
 * full-height sidebar (lg+) + main column. Powered-by footer lives
 * in the main column so the cashier always sees it pinned to the
 * viewport bottom regardless of scroll.
 *
 * Layout strategy (post-2026-05-03 redesign):
 *   - Topbar runs the full viewport width and carries the brand mark
 *     so a long white-label name has unlimited horizontal room
 *     (truncating a partner's name is unacceptable). Sidebar starts
 *     directly underneath at lg+.
 *   - lg+: vertical sidebar shaded ``bg-surface-muted`` (one panel
 *     step above the canvas, stronger than the topbar's ``bg-surface``)
 *     so the column reads as its own zone - solves the "everything
 *     turned white" feedback without a hard tinted fill. A single
 *     collapse toggle sits in the top-right of the sidebar header,
 *     same slot in expanded and collapsed modes; the ``[`` key is the
 *     power-user shortcut. State persists in localStorage.
 *   - < lg: hamburger + slide-in drawer (brand mark stays in the
 *     topbar; drawer header carries it again so the menu reads as a
 *     standalone surface).
 *
 * Active nav item: 3px brand-primary left accent bar (instead of
 * solid-fill tile) - reads cleanly in both expanded and collapsed
 * widths and avoids the "wall of blue" when the sidebar shares a
 * page with other primary-coloured surfaces.
 */
export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [recentOpen, setRecentOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatState, setChatState] = useState<"hidden" | "minimized" | "open">("hidden");
  const { isLocked, lock, unlock } = useInactivityLock();
  // Lazy-mount: only render the ChatDrawer after the cashier first clicks
  // the button. Once mounted it stays in the DOM (preserving conversation
  // history) even when minimized/hidden, but we never allocate the DOM or
  // stream state for sessions where the cashier never opens chat.
  const [chatMounted, setChatMounted] = useState(false);
  const chat = useChatStream(CHAT_URL);
  const [collapsed, toggleCollapsed] = useSidebarCollapse();
  const drawerRef = useFocusTrap<HTMLDivElement>(drawerOpen, () => setDrawerOpen(false));

  // Keep the module-level api lock flag in sync so onUnauthorized doesn't
  // race with the lock screen when a background query returns a 401.
  useEffect(() => {
    setApiLocked(isLocked);
  }, [isLocked]);

  // Auto-close the drawer on every route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // When the AI chat emits a navigate event, open the target in a new tab
  // so the cashier's current workflow isn't interrupted.
  useEffect(() => {
    if (!chat.pendingNavigation) return;
    const route = chat.pendingNavigation;
    chat.consumeNavigation();
    window.open(route, "_blank", "noopener,noreferrer");
  }, [chat]);

  // Lock body scroll while the drawer is open so finger swipes don't
  // accidentally scroll the page underneath.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
    // Don't refetch in the background while the screen is locked.
    // A background 401 would trigger onUnauthorized and race with the
    // LockScreen's own expired-token redirect.
    enabled: !isLocked,
    refetchOnWindowFocus: !isLocked,
  });

  function handleLogout() {
    clearSession();
    navigate("/login", { replace: true });
  }

  if (profile.isPending) {
    return <PageSpinner label={t("common.loading")} />;
  }

  if (profile.isError || !profile.data) {
    return (
      <div className="flex min-h-full items-center justify-center px-4 py-12">
        <Alert tone="error" title={t("shell.loadFailed")}>
          {t("shell.tryAgain")}
        </Alert>
      </div>
    );
  }

  // Blocking MFA gate: manager+ roles must enroll TOTP before using the app.
  if (profile.data.mfa_required && !profile.data.mfa_enrolled) {
    return <MfaSetupGate onEnrolled={() => profile.refetch()} />;
  }

  // Blocking PIN gate: all users must set a PIN so the inactivity lock
  // screen can protect the terminal when they step away (PCI DSS 8.2.8).
  if (!profile.data.has_pin) {
    return <PinSetupGate onPinSet={() => profile.refetch()} />;
  }

  const perms = new Set(profile.data.permissions);
  const features = profile.data.features ?? [];
  const isLocationScope = profile.data.active_membership?.scope_kind === "location";
  const featureGate = (path: string): boolean =>
    !isLocationScope || isFeatureEnabled(features, path);

  const showRecommendations = perms.has(POS_AI_RECOMMENDATIONS_VIEW);
  const showChat = perms.has(POS_AI_CHAT);
  const showSale = perms.has(POS_ORDER_CREATE) && perms.has(POS_PAYMENT_INITIATE);
  const showTransactions = perms.has(POS_TRANSACTION_VIEW) || perms.has(POS_TRANSACTION_VIEW_OWN);
  const showInventory = showSale && featureGate(FEATURE_INVENTORY);
  const showReports =
    (perms.has(POS_METRICS_VIEW_LOCATION) ||
      perms.has(POS_METRICS_VIEW_ORG) ||
      perms.has(POS_METRICS_VIEW_PARTNER) ||
      perms.has(POS_TRANSACTION_VIEW_OWN)) &&
    featureGate(FEATURE_REPORTING);
  // Dashboard surfaces the same feature gate as /reports. ``view_own``
  // callers (cashiers) DO get the page - the backend reuses the
  // batch-out endpoint, which scope-narrows to caller-membership when
  // only ``view_own`` is held. They see their own KPIs / breakdowns;
  // metrics-perm holders see the cross-location rollup.
  const showDashboard =
    (perms.has(POS_METRICS_VIEW_LOCATION) ||
      perms.has(POS_METRICS_VIEW_ORG) ||
      perms.has(POS_METRICS_VIEW_PARTNER) ||
      perms.has(POS_TRANSACTION_VIEW_OWN)) &&
    featureGate(FEATURE_REPORTING);

  const brandName = pickBrandString(profile.data.brand, "product_name") ?? "POS";
  const brandLogo = pickBrandString(profile.data.brand, "logo_url");

  const callerName =
    [profile.data.first_name, profile.data.last_name].filter(Boolean).join(" ").trim() ||
    profile.data.email;

  // The nav-item list is shared between the lg+ sidebar and the
  // mobile drawer. ``collapsed`` only kicks in at lg+; the drawer
  // always shows full labels.
  const renderNavItems = (collapsedRender: boolean): ReactNode => (
    <>
      <NavSection label={t("shell.sectionWorkspace")} collapsed={collapsedRender}>
        {showSale && (
          <NavItem
            to="/sale"
            icon={<ShoppingBag className="h-4 w-4" />}
            label={t("shell.navTakePayment")}
            collapsed={collapsedRender}
          />
        )}
        {showTransactions && (
          <NavItem
            to="/transactions"
            icon={<ListChecks className="h-4 w-4" />}
            label={t("shell.navTransactions")}
            collapsed={collapsedRender}
          />
        )}
        {showInventory && (
          <NavItem
            to="/inventory"
            icon={<Car className="h-4 w-4" />}
            label={t("shell.navInventory")}
            collapsed={collapsedRender}
          />
        )}
        {showDashboard && (
          <NavItem
            to="/dashboard"
            icon={<LayoutDashboard className="h-4 w-4" />}
            label={t("shell.navDashboard")}
            collapsed={collapsedRender}
          />
        )}
        {showReports && (
          <NavItem
            to="/reports"
            icon={<BarChart3 className="h-4 w-4" />}
            label={t("shell.navReports")}
            collapsed={collapsedRender}
          />
        )}
        {showRecommendations && (
          <NavItem
            to="/insights"
            icon={<Sparkles className="h-3 w-3 text-white" />}
            label={t("shell.navRecommendations")}
            collapsed={collapsedRender}
            aiStyle
          />
        )}
      </NavSection>

      <NavSection label={t("shell.sectionAccount")} collapsed={collapsedRender}>
        <NavItem
          to="/profile"
          icon={<ReceiptText className="h-4 w-4" />}
          label={t("shell.navProfile")}
          collapsed={collapsedRender}
        />
      </NavSection>
    </>
  );

  return (
    <div className="flex h-screen flex-col bg-canvas text-foreground">
      <BrandStyles brand={profile.data.brand} />
      <BrandFavicon brand={profile.data.brand} />

      {/* WCAG 4.1.3: live region for dynamic status announcements. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        id="pos-live-region"
      />

      {/* Skip link - visible only on keyboard focus. WCAG 2.4.1. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground focus:shadow-elev-2"
      >
        {t("shell.skipToMain")}
      </a>

      {/* ---- Topbar - full viewport width ----
          Brand mark lives here at every breakpoint so a long
          white-label product name has unlimited horizontal room
          (truncating a partner's name = unacceptable). Right side =
          scope picker + language + theme + user + logout. */}
      <header className="z-20 shrink-0 border-b border-line bg-surface">
        <div className="flex h-14 items-center gap-2 px-3 sm:gap-4 sm:px-4 lg:px-6">
          <div className="flex min-w-0 items-center gap-2">
            {/* Hamburger - replaces the sidebar at < lg. */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={t("shell.openMenu")}
              className="flex h-10 w-10 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-muted hover:text-strong lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Brand surface - visible at every breakpoint. No
                truncation: the topbar is the full viewport width. */}
            <Link
              to="/"
              className="flex min-w-0 items-center gap-2 text-base font-semibold tracking-tight text-strong sm:gap-2.5"
              title={brandName}
            >
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt=""
                  className="h-7 w-auto flex-shrink-0 object-contain sm:h-8"
                />
              ) : (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold uppercase text-primary sm:h-8 sm:w-8"
                  aria-hidden="true"
                >
                  {brandName.trim().charAt(0)}
                </span>
              )}
              <span className="hidden truncate sm:inline">{brandName}</span>
            </Link>
          </div>
          {/* flex-1 spacer pushes the right cluster to the edge */}
          <div className="flex-1" />

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <div className="hidden sm:block">
              <ScopeSwitcher profile={profile.data} />
            </div>
            <span className="mx-1 hidden h-6 w-px bg-line sm:block" aria-hidden="true" />
            <div className="hidden sm:block">
              <LanguagePicker />
            </div>
            <ThemePicker />
            {showChat && (
              <button
                type="button"
                onClick={() => {
                  setChatMounted(true);
                  setChatState("open");
                }}
                title={t("ai.chat.title")}
                aria-label={t("ai.chat.title")}
                aria-expanded={chatState === "open"}
                className="flex h-8 items-center gap-1.5 rounded-full border border-violet-400/30 bg-gray-950 px-3 transition-colors hover:bg-gray-900 active:scale-95 dark:bg-black"
              >
                <Sparkles className="h-3.5 w-3.5 text-violet-300" aria-hidden="true" />
                <span
                  className="text-xs font-bold tracking-wider"
                  style={{
                    background: "linear-gradient(90deg, #c084fc, #818cf8, #38bdf8)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Ask Fin
                </span>
              </button>
            )}
            <Link
              to="/profile"
              title={t("shell.userMenu")}
              aria-label={t("shell.userMenu")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-muted hover:text-strong"
            >
              <User className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={lock}
              title={t("shell.lockTitle")}
              aria-label={t("shell.lockTitle")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-muted hover:text-strong"
            >
              <Lock className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              title={t("shell.signOutTitle")}
              aria-label={t("shell.signOutTitle")}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-muted hover:text-strong"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* ---- Body row: sidebar (lg+) + main column ---- */}
      <div className="flex min-h-0 flex-1">
        {/* ---- Sidebar (lg+) - runs from below the topbar to the
            viewport bottom. ``bg-surface-muted`` is one panel step
            above ``bg-surface`` so the column reads as its own zone
            without a hard tinted fill (2026-05-03 dogfood: "everything
            turned white" - restored visual differentiation here). */}
        <aside
          className={cn(
            "hidden shrink-0 flex-col border-r border-line bg-surface-muted transition-[width] duration-150 lg:flex",
            collapsed ? "w-14" : "w-60",
          )}
        >
          {/* Sidebar header - Option A: empty header, single collapse
              toggle in the top-right (same slot in both modes so the
              control doesn't migrate when the user toggles). */}
          <div
            className={cn(
              "flex h-12 shrink-0 items-center px-3",
              collapsed ? "justify-center px-0" : "justify-end px-3",
            )}
          >
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? t("shell.expandSidebar") : t("shell.collapseSidebar")}
              aria-expanded={!collapsed}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted transition hover:bg-surface hover:text-strong"
            >
              {collapsed ? (
                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Nav - middle of the column, scrolls if it overflows so
              the user row stays anchored at the viewport bottom. */}
          <nav
            className={cn(
              "flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pb-4",
              collapsed && "px-2",
            )}
          >
            {renderNavItems(collapsed)}
          </nav>

          {/* User identity row - flat treatment, pinned to bottom. */}
          <div className="shrink-0 border-t border-line">
            <Link
              to="/profile"
              className={cn(
                "flex items-center gap-2.5 px-3 py-2.5 text-sm transition hover:bg-surface",
                collapsed && "justify-center px-0 py-3",
              )}
              title={collapsed ? `${callerName} - ${profile.data.email}` : undefined}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-[0.6875rem] font-semibold uppercase text-accent-foreground">
                {(callerName || "U").trim().charAt(0)}
              </span>
              {!collapsed && (
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-strong">
                    {callerName}
                  </span>
                  <span className="block truncate text-[0.6875rem] text-muted">
                    {profile.data.email}
                  </span>
                </span>
              )}
            </Link>
          </div>
        </aside>

        {/* ---- Right column: main + footer ---- */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
              <main id="main" className="min-w-0">
                <Outlet
                  context={
                    { openRecentPanel: () => setRecentOpen(true) } satisfies AppShellOutletContext
                  }
                />
              </main>
            </div>
          </div>
          {/* Footer pinned at the viewport bottom (outside the scroll
              region) so the cashier always sees Powered by + T&Cs
              without scrolling. */}
          <Footer brand={profile.data.brand} className="shrink-0" />
        </div>
      </div>

      {/* ---- Mobile / tablet nav drawer (< lg) ----
          Starts at ``top-14`` so the global topbar stays visible while
          the drawer is open - consistent with the Recent Transactions
          panel and Timeline Payment drawer (2026-05-03 dogfood: "all
          drawers should render below the header"). The drawer's own
          header still shows the brand name as a redundant cue. */}
      {drawerOpen && (
        <div
          ref={drawerRef}
          className="fixed inset-x-0 bottom-0 top-14 z-40 lg:hidden"
          aria-modal="true"
          role="dialog"
          aria-label={t("shell.menu")}
        >
          {/* Scrim */}
          <button
            type="button"
            aria-label={t("shell.closeMenu")}
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />
          {/* Panel slides in from the left */}
          <aside className="absolute inset-y-0 left-0 flex w-[80vw] max-w-xs flex-col bg-canvas shadow-elev-3">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <span className="text-sm font-semibold text-strong">{brandName}</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label={t("shell.closeMenu")}
                className="flex h-9 w-9 items-center justify-center rounded-md text-muted hover:bg-surface-muted hover:text-strong"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">
              <div className="flex flex-col gap-3 sm:hidden">
                <div>
                  <p className="mb-1 px-1 text-[0.625rem] font-semibold uppercase tracking-wider text-muted">
                    {t("shell.sectionScope")}
                  </p>
                  <ScopeSwitcher profile={profile.data} />
                </div>
                <div>
                  <p className="mb-1 px-1 text-[0.625rem] font-semibold uppercase tracking-wider text-muted">
                    {t("shell.sectionLanguage")}
                  </p>
                  <LanguagePicker />
                </div>
              </div>
              {renderNavItems(false)}
              <Link
                to="/profile"
                className="mt-auto flex items-center gap-3 rounded-lg bg-surface p-3 shadow-elev-1 transition hover:shadow-elev-2"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold uppercase text-accent-foreground">
                  {(callerName || "U").trim().charAt(0)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-strong">
                    {callerName}
                  </span>
                  <span className="block truncate text-[0.6875rem] text-muted">
                    {profile.data.email}
                  </span>
                </span>
              </Link>
            </div>
          </aside>
        </div>
      )}

      <RecentTransactionsPanel open={recentOpen} onClose={() => setRecentOpen(false)} />
      {chatMounted && (
        <ChatDrawer
          isOpen={chatState === "open"}
          onOpen={() => setChatState("open")}
          onMinimize={() => setChatState("minimized")}
          onClose={() => setChatState("hidden")}
          chat={chat}
        />
      )}
      {isLocked && <LockScreen hasPin={profile.data.has_pin} onUnlock={unlock} />}
    </div>
  );
}

export interface AppShellOutletContext {
  readonly openRecentPanel: () => void;
}

function NavSection({
  label,
  collapsed,
  children,
}: {
  readonly label: string;
  readonly collapsed: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      {/* Section label hides entirely in collapsed mode - relying on
          icon grouping + a thin separator line below would add visual
          weight; just dropping the heading keeps the column clean. */}
      {!collapsed && (
        <p className="px-3 pb-1 text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-muted">
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  collapsed,
  aiStyle = false,
}: {
  readonly to: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly collapsed: boolean;
  readonly aiStyle?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          // 3px brand-primary left accent bar replaces the prior
          // solid-fill active tile. Reads cleanly in both expanded
          // and collapsed widths and avoids the saturation pile-up
          // on pages whose primary CTAs are also brand-primary.
          "relative flex min-h-tap items-center gap-3 rounded-lg pl-3 pr-3 text-sm font-medium transition-colors",
          collapsed && "justify-center px-0",
          isActive
            ? "bg-primary/5 font-semibold text-strong before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-primary before:content-['']"
            : "text-foreground hover:bg-surface hover:text-strong",
        )
      }
    >
      {aiStyle ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-indigo-500">
          {icon}
        </span>
      ) : (
        <span className="shrink-0">{icon}</span>
      )}
      {!collapsed && (
        <span className="flex min-w-0 items-center gap-2 truncate">
          <span className="truncate">{label}</span>
          {aiStyle && (
            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
              AI
            </span>
          )}
        </span>
      )}
    </NavLink>
  );
}

function pickBrandString(brand: Record<string, unknown>, key: string): string | null {
  const v = brand?.[key];
  return typeof v === "string" && v.trim().length > 0 ? v : null;
}
